import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiAccount, mdiApps, mdiArrowRight, mdiPlus, mdiTicketOutline, mdiTrashCanOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog'
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog'
import { Alert, AlertDescription } from '../ui/alert'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useTagsWithCounts, useCreateTag, useUpdateTag, useDeleteTag, useTagEntities, type Tag } from '../../api/tags'
import { parseApiError } from '../../lib/apiError'
import { TagBadge } from './TagBadge'
import { TagColorPicker } from './TagColorPicker'
import { Can, useCan } from '../../contexts/PermissionsContext'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { mdiTagOutline } from '@mdi/js'

// interpretTagError maps a create/update tag mutation error to either a
// field-level message (shown under the code input) or a general one (shown
// as a destructive Alert inside the dialog/form) — never both, and never
// silently swallowed. `t` is the `common` namespace translation function.
function interpretTagError(error: unknown, t: (key: string) => string): { fieldError: string | null; generalError: string | null } {
    const payload = parseApiError(error)
    if (payload?.code === 'INVALID_CODE_FORMAT') {
        const field = payload.fields?.find(f => f.name === 'code')
        return { fieldError: field?.message || t('tags.errors.invalid_code'), generalError: null }
    }
    if (payload?.code === 'DUPLICATE_ENTRY') {
        return { fieldError: null, generalError: t('tags.errors.duplicate') }
    }
    if (payload?.code === 'TAG_LIMIT_REACHED') {
        return { fieldError: null, generalError: t('tags.errors.limit') }
    }
    return { fieldError: null, generalError: t('tags.errors.generic') }
}

const ENTITY_TYPE_OPTIONS = [
    { value: 'asset', label: 'Asset' },
    { value: 'document', label: 'Document' },
    { value: 'user', label: 'User' },
    { value: 'contract', label: 'Contract' },
    { value: 'issue', label: 'Issue' },
    { value: 'expense', label: 'Expense' },
    { value: 'conversation', label: 'Conversation' },
] as const

const ENTITY_TYPES = ENTITY_TYPE_OPTIONS.map(opt => opt.value)
type EntityType = (typeof ENTITY_TYPES)[number]

function entityTypeIconPath(type: string): string {
    switch (type) {
        case 'asset':
            return SECTION_ICONS.assets
        case 'document':
            return SECTION_ICONS.documents
        case 'user':
            return mdiAccount
        case 'contract':
            return SECTION_ICONS.contracts
        case 'issue':
            return mdiTicketOutline
        case 'expense':
            return SECTION_ICONS.expenses
        case 'conversation':
            return SECTION_ICONS.conversations
        default:
            return mdiApps
    }
}

function entityHref(org: string, row: { entity_type: string; entity_uid: string }): string {
    switch (row.entity_type) {
        case 'asset':
            return `/${org}/assets/${row.entity_uid}`
        case 'document':
            return `/${org}/documents/${row.entity_uid}`
        case 'user':
            return `/${org}/users/${row.entity_uid}`
        case 'contract':
            return `/${org}/contracts/${row.entity_uid}`
        case 'issue':
            return `/${org}/issues/${row.entity_uid}`
        case 'expense':
            return `/${org}/expenses/${row.entity_uid}`
        case 'conversation':
            return `/${org}/conversations?conversation=${row.entity_uid}`
        default:
            return `/${org}`
    }
}

export interface TagsManagementPageSearch {
    type?: 'all' | EntityType
    q?: string
    page?: number
}

interface TagsManagementPageProps {
    org: string
    tagId?: string // UID or code
    search?: TagsManagementPageSearch
}

export function TagsManagementPage({ org, tagId, search }: TagsManagementPageProps) {
    const navigate = useNavigate()
    const { t } = useTranslation('common')

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [newTag, setNewTag] = useState({ name: '', code: '', description: '', color: '#6366f1', target_entity_types: [] as string[] })
    const [createFieldError, setCreateFieldError] = useState<string | null>(null)
    const [createGeneralError, setCreateGeneralError] = useState<string | null>(null)

    // Edit form state
    const [editName, setEditName] = useState('')
    const [editCode, setEditCode] = useState('')
    const [editDescription, setEditDescription] = useState('')
    const [editColor, setEditColor] = useState('')
    const [editTargetEntityTypes, setEditTargetEntityTypes] = useState<string[]>([])
    const [listFilter, setListFilter] = useState('')

    // Queries — include usage counts so each row shows how many entities carry it.
    const { data: tags = [], isLoading } = useTagsWithCounts(org)
    const createTagMutation = useCreateTag(org)
    const deleteTagMutation = useDeleteTag(org)

    // Resolve tagId (UID or code) to a tag
    const selectedTag = useMemo(() => {
        if (!tagId) return null
        return tags.find(t => t.uid === tagId) || tags.find(t => t.code === tagId) || null
    }, [tags, tagId])

    // Set breadcrumbs — append the tag name on the detail view, matching the
    // old dedicated browse page's Home → Tags → <tag name> trail. The last
    // crumb represents the current page and has no `path` (non-clickable).
    const breadcrumbs = useMemo(() => {
        if (tagId) {
            return [
                { title: t('nav.home'), path: `/${org}` },
                { title: t('nav.settings'), path: `/${org}/settings` },
                { title: t('tags.title'), path: `/${org}/settings/tags` },
                { title: selectedTag?.name || tagId },
            ]
        }
        return [
            { title: t('nav.home'), path: `/${org}` },
            { title: t('nav.settings'), path: `/${org}/settings` },
            { title: t('tags.title') },
        ]
    }, [org, t, tagId, selectedTag])

    useBreadcrumbs(breadcrumbs)

    // Client-side name/code/description filter over the tag list.
    const filteredTags = useMemo(() => {
        if (!listFilter.trim()) return tags
        const q = listFilter.toLowerCase()
        return tags.filter(
            (tg) =>
                tg.name?.toLowerCase().includes(q) ||
                tg.code?.toLowerCase().includes(q) ||
                tg.description?.toLowerCase().includes(q),
        )
    }, [tags, listFilter])

    // Sync edit form with selected tag
    useEffect(() => {
        if (selectedTag) {
            setEditName(selectedTag.name || '')
            setEditCode(selectedTag.code || '')
            setEditDescription(selectedTag.description || '')
            setEditColor(selectedTag.color || '#6366f1')
            setEditTargetEntityTypes(selectedTag.target_entity_types || [])
        }
    }, [selectedTag])

    const navigateToTag = (tag: Tag | null) => {
        if (tag?.code) {
            navigate({ to: '/$org/settings/tags/$tagId', params: { org, tagId: tag.code } })
        } else if (tag?.uid) {
            navigate({ to: '/$org/settings/tags/$tagId', params: { org, tagId: tag.uid } })
        } else {
            navigate({ to: '/$org/settings/tags', params: { org } })
        }
    }

    const closeCreateDialog = () => {
        setIsCreateDialogOpen(false)
        setCreateFieldError(null)
        setCreateGeneralError(null)
    }

    const handleCreate = () => {
        setCreateFieldError(null)
        setCreateGeneralError(null)
        createTagMutation.mutate(
            {
                name: newTag.name,
                code: newTag.code || undefined,
                description: newTag.description || undefined,
                color: newTag.color || undefined,
                target_entity_types: newTag.target_entity_types.length > 0
                    ? newTag.target_entity_types as EntityType[]
                    : undefined,
            },
            {
                onSuccess: (data) => {
                    closeCreateDialog()
                    setNewTag({ name: '', code: '', description: '', color: '#6366f1', target_entity_types: [] })
                    if (data) navigateToTag(data)
                },
                onError: (error) => {
                    const { fieldError, generalError } = interpretTagError(error, t)
                    setCreateFieldError(fieldError)
                    setCreateGeneralError(generalError)
                },
            }
        )
    }

    const handleDelete = () => {
        if (!selectedTag?.uid) return
        deleteTagMutation.mutate(selectedTag.uid, {
            onSuccess: () => {
                setIsDeleteDialogOpen(false)
                navigate({ to: '/$org/settings/tags', params: { org } })
            },
        })
    }

    const handleToggleEntityType = (type: string, checked: boolean, isEdit: boolean) => {
        if (isEdit) {
            setEditTargetEntityTypes(prev =>
                checked ? [...prev, type] : prev.filter(t => t !== type)
            )
        } else {
            setNewTag(prev => ({
                ...prev,
                target_entity_types: checked
                    ? [...prev.target_entity_types, type]
                    : prev.target_entity_types.filter(t => t !== type),
            }))
        }
    }

    return (
        <div className="flex flex-col h-screen" data-testid="tag-management-page">
            {/* Header */}
            <div className="border-b p-4">
                <PageHeader
                    icon={SECTION_ICONS.tags}
                    title={t('tags.title')}
                    description={t('tags.create')}
                    actions={
                    <Dialog open={isCreateDialogOpen} onOpenChange={(open) => (open ? setIsCreateDialogOpen(true) : closeCreateDialog())}>
                        <Can perm="configuration.write">
                          <DialogTrigger asChild>
                              <Button data-testid="create-tag-button">
                                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                  {t('tags.create')}
                              </Button>
                          </DialogTrigger>
                        </Can>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{t('tags.create')}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 p-4">
                                {createGeneralError && (
                                    <Alert variant="destructive" data-testid="create-tag-error">
                                        <AlertDescription>{createGeneralError}</AlertDescription>
                                    </Alert>
                                )}
                                <div>
                                    <Label htmlFor="tag-name">{t('tags.name')}</Label>
                                    <Input
                                        id="tag-name"
                                        value={newTag.name}
                                        onChange={(e) => setNewTag(prev => ({ ...prev, name: e.target.value }))}
                                        data-testid="create-tag-name"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="tag-code">{t('tags.code')}</Label>
                                    <Input
                                        id="tag-code"
                                        value={newTag.code}
                                        onChange={(e) => {
                                            setNewTag(prev => ({ ...prev, code: e.target.value }))
                                            if (createFieldError) setCreateFieldError(null)
                                        }}
                                        aria-invalid={!!createFieldError}
                                        aria-describedby={createFieldError ? 'create-tag-code-error' : 'create-tag-code-hint'}
                                        data-testid="create-tag-code"
                                    />
                                    {createFieldError ? (
                                        <p id="create-tag-code-error" data-testid="create-tag-code-error" className="text-xs text-destructive mt-1">
                                            {createFieldError}
                                        </p>
                                    ) : (
                                        <p id="create-tag-code-hint" className="text-xs text-muted-foreground mt-1">
                                            {t('tags.code_format_hint')}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="tag-description">{t('tags.description')}</Label>
                                    <Textarea
                                        id="tag-description"
                                        value={newTag.description}
                                        onChange={(e) => setNewTag(prev => ({ ...prev, description: e.target.value }))}
                                        data-testid="create-tag-description"
                                    />
                                </div>
                                <div>
                                    <Label>{t('tags.color')}</Label>
                                    <TagColorPicker
                                        value={newTag.color}
                                        onChange={(color) => setNewTag(prev => ({ ...prev, color }))}
                                    />
                                </div>
                                <div>
                                    <Label>{t('tags.target_entity_types')}</Label>
                                    <div className="flex flex-wrap gap-3 mt-2">
                                        {ENTITY_TYPE_OPTIONS.map((opt) => (
                                            <label key={opt.value} className="flex items-center gap-2 text-sm">
                                                <Checkbox
                                                    checked={newTag.target_entity_types.includes(opt.value)}
                                                    onCheckedChange={(checked) => handleToggleEntityType(opt.value, !!checked, false)}
                                                    data-testid={`create-entity-type-${opt.value}`}
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={closeCreateDialog}
                                        disabled={createTagMutation.isPending}
                                    >
                                        {t('actions.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleCreate}
                                        disabled={createTagMutation.isPending || !newTag.name}
                                        data-testid="create-tag-submit"
                                    >
                                        {createTagMutation.isPending ? '...' : t('actions.create')}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                    }
                />
            </div>

            {/* Two-panel layout */}
            <div className="flex flex-1 overflow-hidden">
                {/* Left panel - List */}
                <div className="w-1/3 border-r overflow-y-auto flex flex-col">
                    <div className="p-3 border-b shrink-0">
                        <Input
                            value={listFilter}
                            onChange={(e) => setListFilter(e.target.value)}
                            placeholder={t('tags.search_placeholder')}
                            data-testid="tags-list-filter"
                        />
                    </div>
                    {isLoading ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            Loading...
                        </div>
                    ) : filteredTags.length === 0 ? (
                        <EmptyState variant="inline" live icon={mdiTagOutline} title={t('tags.no_tags')} />
                    ) : (
                        <div className="divide-y divide-border">
                            {filteredTags.map((tag) => (
                                <button
                                    key={tag.uid}
                                    data-testid={`tag-item-${tag.uid}`}
                                    className={`w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors flex items-center gap-3 ${selectedTag?.uid === tag.uid ? 'bg-accent' : ''}`}
                                    onClick={() => navigateToTag(tag)}
                                >
                                    <span
                                        className="h-4 w-4 rounded-full shrink-0"
                                        style={{ backgroundColor: tag.color || '#6366f1' }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-foreground truncate">{tag.name}</div>
                                        {tag.code && (
                                            <div className="text-xs text-muted-foreground font-mono truncate">{tag.code}</div>
                                        )}
                                    </div>
                                    <span
                                        className="text-xs text-muted-foreground shrink-0"
                                        data-testid={`tag-item-usage-${tag.uid}`}
                                    >
                                        {t('tags.entities_count', { count: tag.usage_count ?? 0 })}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right panel - Editor + entity browser */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {selectedTag ? (
                        <>
                            <TagEditor
                                org={org}
                                tag={selectedTag}
                                editName={editName}
                                setEditName={setEditName}
                                editCode={editCode}
                                setEditCode={setEditCode}
                                editDescription={editDescription}
                                setEditDescription={setEditDescription}
                                editColor={editColor}
                                setEditColor={setEditColor}
                                editTargetEntityTypes={editTargetEntityTypes}
                                onToggleEntityType={(type, checked) => handleToggleEntityType(type, checked, true)}
                                onDelete={() => setIsDeleteDialogOpen(true)}
                            />
                            <TagEntityBrowser
                                org={org}
                                tagId={tagId!}
                                tag={selectedTag}
                                search={search}
                            />
                        </>
                    ) : (
                        <EmptyState
                            variant="inline"
                            className="h-full justify-center"
                            icon={mdiTagOutline}
                            title={t('tags.no_tags')}
                        />
                    )}
                </div>
            </div>

            {/* Delete Confirmation */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('tags.delete')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('tags.delete_confirm')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-tag-cancel">{t('actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-tag-confirm"
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteTagMutation.isPending ? '...' : t('actions.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

function TagEditor({
    org,
    tag,
    editName,
    setEditName,
    editCode,
    setEditCode,
    editDescription,
    setEditDescription,
    editColor,
    setEditColor,
    editTargetEntityTypes,
    onToggleEntityType,
    onDelete,
}: {
    org: string
    tag: Tag
    editName: string
    setEditName: (v: string) => void
    editCode: string
    setEditCode: (v: string) => void
    editDescription: string
    setEditDescription: (v: string) => void
    editColor: string
    setEditColor: (v: string) => void
    editTargetEntityTypes: string[]
    onToggleEntityType: (type: string, checked: boolean) => void
    onDelete: () => void
}) {
    const { t } = useTranslation('common')
    const updateTagMutation = useUpdateTag(org, tag.uid!)
    const [editFieldError, setEditFieldError] = useState<string | null>(null)
    const [editGeneralError, setEditGeneralError] = useState<string | null>(null)
    // Editing the tag *catalogue* (rename, recolor, retarget, delete) is
    // `configuration.write`. Assigning an existing tag to an entity is a
    // different permission and lives with the entity, not here. Without the
    // permission the form stays on screen read-only so the tag's definition
    // remains readable (spec 2026-09-04-02, "Gating").
    const canWriteCatalogue = useCan('configuration.write')

    // Navigating to a different tag must not carry over a stale error from
    // the previous one.
    useEffect(() => {
        setEditFieldError(null)
        setEditGeneralError(null)
    }, [tag.uid])

    const hasChanges =
        editName !== (tag.name || '') ||
        editCode !== (tag.code || '') ||
        editDescription !== (tag.description || '') ||
        editColor !== (tag.color || '#6366f1') ||
        JSON.stringify(editTargetEntityTypes.sort()) !== JSON.stringify((tag.target_entity_types || []).sort())

    const handleSave = () => {
        setEditFieldError(null)
        setEditGeneralError(null)
        updateTagMutation.mutate({
            name: editName || undefined,
            code: editCode || undefined,
            description: editDescription || undefined,
            color: editColor || undefined,
            target_entity_types: editTargetEntityTypes.length > 0
                ? editTargetEntityTypes as EntityType[]
                : undefined,
        }, {
            onError: (error) => {
                const { fieldError, generalError } = interpretTagError(error, t)
                setEditFieldError(fieldError)
                setEditGeneralError(generalError)
            },
        })
    }

    return (
        <div className="space-y-6 max-w-lg">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <TagBadge tag={{ uid: tag.uid!, name: editName || tag.name || '', color: editColor }} />
                    <h2 className="text-lg font-semibold text-foreground">{t('tags.edit')}</h2>
                </div>
                {canWriteCatalogue && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDelete}
                        data-testid="delete-tag-button"
                        className="text-destructive hover:text-destructive"
                    >
                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                    </Button>
                )}
            </div>

            <div className="space-y-4">
                {editGeneralError && (
                    <Alert variant="destructive" data-testid="edit-tag-error">
                        <AlertDescription>{editGeneralError}</AlertDescription>
                    </Alert>
                )}
                <div>
                    <Label htmlFor="edit-name">{t('tags.name')}</Label>
                    <Input
                        id="edit-name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        readOnly={!canWriteCatalogue}
                        data-testid="edit-tag-name"
                    />
                </div>
                <div>
                    <Label htmlFor="edit-code">{t('tags.code')}</Label>
                    <Input
                        id="edit-code"
                        value={editCode}
                        onChange={(e) => {
                            setEditCode(e.target.value)
                            if (editFieldError) setEditFieldError(null)
                        }}
                        readOnly={!canWriteCatalogue}
                        aria-invalid={!!editFieldError}
                        aria-describedby={editFieldError ? 'edit-tag-code-error' : 'edit-tag-code-hint'}
                        data-testid="edit-tag-code"
                    />
                    {editFieldError ? (
                        <p id="edit-tag-code-error" data-testid="edit-tag-code-error" className="text-xs text-destructive mt-1">
                            {editFieldError}
                        </p>
                    ) : (
                        <p id="edit-tag-code-hint" className="text-xs text-muted-foreground mt-1">
                            {t('tags.code_format_hint')}
                        </p>
                    )}
                </div>
                <div>
                    <Label htmlFor="edit-description">{t('tags.description')}</Label>
                    <Textarea
                        id="edit-description"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        readOnly={!canWriteCatalogue}
                        data-testid="edit-tag-description"
                    />
                </div>
                <div>
                    <Label>{t('tags.color')}</Label>
                    {canWriteCatalogue ? (
                        <TagColorPicker value={editColor} onChange={setEditColor} />
                    ) : (
                        <div className="flex items-center gap-2 mt-2" data-testid="tag-color-readonly">
                            <span
                                className="h-6 w-6 rounded-full border border-border"
                                style={{ backgroundColor: editColor || '#6366f1' }}
                            />
                            <span className="text-sm font-mono text-muted-foreground">
                                {editColor || '#6366f1'}
                            </span>
                        </div>
                    )}
                </div>
                <div>
                    <Label>{t('tags.target_entity_types')}</Label>
                    <div className="flex flex-wrap gap-3 mt-2">
                        {ENTITY_TYPE_OPTIONS.map((opt) => (
                            <label key={opt.value} className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={editTargetEntityTypes.includes(opt.value)}
                                    onCheckedChange={(checked) => onToggleEntityType(opt.value, !!checked)}
                                    disabled={!canWriteCatalogue}
                                    data-testid={`edit-entity-type-${opt.value}`}
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                </div>

                {canWriteCatalogue && hasChanges && (
                    <div className="flex gap-2 pt-2">
                        <Button
                            onClick={handleSave}
                            disabled={updateTagMutation.isPending || !editName}
                            data-testid="save-tag-button"
                        >
                            {updateTagMutation.isPending ? '...' : t('actions.save')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

function TagEntityBrowser({
    org,
    tagId,
    tag,
    search,
}: {
    org: string
    tagId: string
    tag: Tag
    search?: TagsManagementPageSearch
}) {
    const { t } = useTranslation('common')

    const activeType = (search?.type === 'all' ? undefined : search?.type) as EntityType | undefined
    const q = search?.q
    const page = search?.page || 1

    const { data: entities, isLoading } = useTagEntities(org, tag.uid || '', {
        entity_type: activeType,
        q,
        page,
        limit: 50,
    })

    const counts = entities?.counts_by_type || {}
    const totalAll = Object.values(counts).reduce((s: number, n) => s + (n as number), 0)

    return (
        <div className="space-y-4" data-testid="tag-entity-browser">
            <h3 className="text-sm font-semibold text-foreground">{t('tags.browse_title')}</h3>

            <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
                <TabButton org={org} tagId={tagId} type="all" label={t('tags.tab_all')} count={totalAll} active={!search?.type || search?.type === 'all'} />
                {ENTITY_TYPES.map((type) => (
                    <TabButton
                        key={type}
                        org={org}
                        tagId={tagId}
                        type={type}
                        label={t(`tags.tab_${type}`)}
                        count={counts[type] || 0}
                        active={search?.type === type}
                    />
                ))}
            </div>

            {isLoading ? (
                <div className="text-sm text-muted-foreground">{t('common:actions.loading')}</div>
            ) : !entities?.data || entities.data.length === 0 ? (
                <EmptyState
                    variant="inline"
                    live
                    icon={mdiTagOutline}
                    title={t('tags.no_entities')}
                    data-testid="tag-browse-empty"
                />
            ) : (
                <div className="divide-y divide-border">
                    {entities.data.map((row) => (
                        <EntityRow
                            key={`${row.entity_type}-${row.entity_uid}`}
                            org={org}
                            row={{
                                entity_type: row.entity_type || '',
                                entity_uid: row.entity_uid || '',
                                name: row.name || '',
                                code: row.code,
                                sub_label: row.sub_label,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function TabButton({
    org,
    tagId,
    type,
    label,
    count,
    active,
}: {
    org: string
    tagId: string
    type: string
    label: string
    count: number
    active: boolean
}) {
    return (
        <Link
            to="/$org/settings/tags/$tagId"
            params={{ org, tagId }}
            search={type === 'all' ? {} : { type: type as EntityType }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            data-testid={`tag-browse-tab-${type}`}
        >
            <AppIcon path={entityTypeIconPath(type)} size="sm" />
            <span>{label}</span>
            <span className="text-xs opacity-75">({count})</span>
        </Link>
    )
}

function EntityRow({
    org,
    row,
}: {
    org: string
    row: {
        entity_type: string
        entity_uid: string
        name: string
        code?: string | null
        sub_label?: string
    }
}) {
    const target = entityHref(org, row)
    return (
        <a
            href={target}
            className="flex items-center justify-between py-3 px-2 hover:bg-muted/50"
            data-testid={`tag-browse-row-${row.entity_type}-${row.entity_uid}`}
        >
            <div className="flex items-center gap-3">
                <span
                    className="inline-flex items-center text-muted-foreground shrink-0"
                    title={row.entity_type}
                    aria-label={row.entity_type}
                >
                    <AppIcon path={entityTypeIconPath(row.entity_type)} size="md" />
                </span>
                <span className="text-sm text-foreground">{row.name || '—'}</span>
                {row.code && <span className="text-xs text-muted-foreground font-mono">{row.code}</span>}
                {row.sub_label && (
                    <span className="text-xs text-muted-foreground italic">{row.sub_label}</span>
                )}
            </div>
            <AppIcon path={mdiArrowRight} size="sm" />
        </a>
    )
}
